import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LoadingOverlay, useAlert } from '../../components';
import { getTeamData } from '../../services/firestoreService';
import { formatCurrency, copyToClipboard } from '../../utils/helpers';
import './ReferralScreen.css';

// Helper function to mask phone number
const maskPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return 'N/A';
    if (phoneNumber.length >= 10) {
        return phoneNumber.slice(0, 4) + '***' + phoneNumber.slice(-3);
    }
    return phoneNumber;
};

export const ReferralScreen = () => {
    const navigate = useNavigate();
    const { userData } = useAuth();
    const alert = useAlert();
    const [teamData, setTeamData] = useState({ level1: [], level2: [], level3: [] });
    const [loading, setLoading] = useState(true);
    const [activeLevel, setActiveLevel] = useState('B');

    useEffect(() => {
        loadTeamData();
    }, []);

    const loadTeamData = async () => {
        if (!userData?.uid) return;

        try {
            const data = await getTeamData(userData.uid, userData.referralCode);
            setTeamData(data);
        } catch (error) {
            console.error('Error loading team data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCopyLink = async () => {
        const link = `${window.location.origin}/signup?ref=${userData?.referralCode}`;
        const success = await copyToClipboard(link);
        if (success) {
            alert.showSuccess('Copied', 'Referral link copied to clipboard');
        }
    };

    const levels = [
        { key: 'B', label: 'B', rebate: '9%', data: teamData.level1 },
        { key: 'C', label: 'C', rebate: '4%', data: teamData.level2 },
        { key: 'D', label: 'D', rebate: '2%', data: teamData.level3 },
    ];

    const totalMembers = teamData.level1.length + teamData.level2.length + teamData.level3.length;

    if (loading) {
        return <LoadingOverlay visible message="Loading team data..." />;
    }

    const activeLevelData = levels.find(l => l.key === activeLevel);

    return (
        <div className="referral-screen-modern">
            <div className="referral-header-modern">
                <h1 className="referral-title-main">MY NETWORK</h1>
                <p className="referral-subtitle-main">MANAGE YOUR DOWNLINE & COMMISSIONS</p>
            </div>

            <div className="referral-stats-grid">
                <div className="referral-stat-card-new">
                    <span className="referral-stat-label">TOTAL TEAM</span>
                    <span className="referral-stat-value-big">{totalMembers}</span>
                    <div className="deco-icon">
                        <span className="material-icons">groups</span>
                    </div>
                </div>
                <div className="referral-stat-card-new">
                    <span className="referral-stat-label">TEAM EARNING</span>
                    <span className="referral-stat-value-big green">{formatCurrency(userData?.totalTeamEarnings || 0)}</span>
                    <div className="deco-icon">
                        <span className="material-icons">payments</span>
                    </div>
                </div>
            </div>

            <div className="invitation-link-card">
                <h2 className="link-card-title">INVITATION LINK</h2>
                <div className="link-input-container">
                    <div className="link-text-box">
                        {`${window.location.origin}/register?ref=${userData?.referralCode || ''}`}
                    </div>
                    <button className="copy-link-btn" onClick={handleCopyLink}>COPY</button>
                </div>
            </div>

            <div className="rebate-levels-row">
                {levels.map((level) => (
                    <div
                        key={level.key}
                        className={`rebate-level-box ${activeLevel === level.key ? 'active' : ''}`}
                        onClick={() => setActiveLevel(level.key)}
                    >
                        <span className="level-label-top">LEVEL {level.label} ({level.data.length})</span>
                        <span className="level-name-big">{level.label}</span>
                        <div className="rebate-badge">{level.rebate} Rebate</div>
                    </div>
                ))}
            </div>

            <div className="downline-list-container">
                {activeLevelData.data.length > 0 ? (
                    activeLevelData.data.map((member, idx) => (
                        <div key={idx} className="downline-member-card">
                            <div className="member-info-left">
                                <div className="member-icon-circle">
                                    <span className="material-icons">account_circle</span>
                                </div>
                                <div className="member-text-group">
                                    <span className="member-phone-masked">{maskPhoneNumber(member.phoneNumber)}</span>
                                    <span className="member-date-subtitle">{member.createdAt ? new Date(member.createdAt.seconds * 1000).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '24 OCT 2023'}</span>
                                </div>
                            </div>
                            <div className="member-investment-right">
                                <span className="investment-label">INVESTMENT</span>
                                <span className="investment-value">{formatCurrency(member.totalRecharged || 0)}</span>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="empty-downline">
                        <span className="material-icons">group_off</span>
                        <p>No members in Level {activeLevel}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReferralScreen;
